<template>
  <BaseNavigationBar title="Редактирование шаблона" />

  <BaseIsland size="lg">
    <form class="two-template-edit" @submit.prevent="handleSave">
      <div class="two-template-edit__header">
        <BaseForm
          ref="formRef1"
          v-model="formModel"
          :schema="twoTemplateHeaderSchema"
          :with-form-wrapper="false"
          :with-error-message="false"
        />
        <TemplateLandingButtonSlot
          :button="buttons.header"
          @add="handleStartAddButton('header')"
          @edit="handleOpenModuleSelect('header')"
          @clear="handleClearButton('header')"
        />
      </div>

      <div class="two-template-edit__main-section">
        <div class="two-template-edit__main-image">
          <TemplateMediaSlot
            :model-value="images[0]"
            class="two-template-edit__media-slot"
            @update:model-value="(urls) => handleUpdateImage(0, urls)"
          />
        </div>
        <div class="two-template-edit__side-images">
          <TemplateMediaSlot
            :model-value="images[1]"
            class="two-template-edit__media-slot"
            @update:model-value="(urls) => handleUpdateImage(1, urls)"
          />
          <TemplateMediaSlot
            :model-value="images[2]"
            class="two-template-edit__media-slot"
            @update:model-value="(urls) => handleUpdateImage(2, urls)"
          />
        </div>
      </div>

      <div class="two-template-edit__dual-headers">
        <div class="two-template-edit__header-left">
          <BaseForm
            ref="formRef2"
            v-model="formModel"
            :schema="twoTemplateLeftHeaderSchema"
            :with-form-wrapper="false"
            :with-error-message="false"
          />
          <TemplateLandingButtonSlot
            full-width
            :button="buttons.leftHeader"
            @add="handleStartAddButton('leftHeader')"
            @edit="handleOpenModuleSelect('leftHeader')"
            @clear="handleClearButton('leftHeader')"
          />
        </div>
        <div class="two-template-edit__header-right">
          <BaseForm
            ref="formRef3"
            v-model="formModel"
            :schema="twoTemplateRightHeaderSchema"
            :with-form-wrapper="false"
            :with-error-message="false"
          />
          <TemplateLandingButtonSlot
            full-width
            :button="buttons.rightHeader"
            @add="handleStartAddButton('rightHeader')"
            @edit="handleOpenModuleSelect('rightHeader')"
            @clear="handleClearButton('rightHeader')"
          />
        </div>
      </div>

      <div class="two-template-edit__triple-images">
        <TemplateMediaSlot
          :model-value="images[3]"
          class="two-template-edit__media-slot"
          @update:model-value="(urls) => handleUpdateImage(3, urls)"
        />
        <TemplateMediaSlot
          :model-value="images[4]"
          class="two-template-edit__media-slot"
          @update:model-value="(urls) => handleUpdateImage(4, urls)"
        />
        <TemplateMediaSlot
          :model-value="images[5]"
          class="two-template-edit__media-slot"
          @update:model-value="(urls) => handleUpdateImage(5, urls)"
        />
      </div>

      <!-- Дополнительный блок с заголовком и описанием -->
      <div class="two-template-edit__additional-section">
        <BaseForm
          ref="formRef4"
          v-model="formModel"
          :schema="twoTemplateAdditionalSectionSchema"
          :with-form-wrapper="false"
          :with-error-message="false"
        />
        <TemplateLandingButtonSlot
          :button="buttons.additional"
          @add="handleStartAddButton('additional')"
          @edit="handleOpenModuleSelect('additional')"
          @clear="handleClearButton('additional')"
        />
      </div>

      <BaseButton
        variant="primary"
        type="submit"
        max-width="335px"
        with-full
        with-decor
        :loading="loading.value"
        :disabled="loading.value"
      >
        Сохранить
      </BaseButton>
    </form>
  </BaseIsland>

  <LandingButtonTypePopup
    v-model="isButtonTypePopupOpen"
    @select-module="handleChooseButtonType('module')"
    @select-object="handleChooseButtonType('moduleObject')"
  />
</template>

<script setup lang="ts">
import type { ITwoTemplateData, ITwoTemplateFormData } from '~/entities/projects/model/templates-interface'
import type { IFormExpose } from '~/shared/ui'
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useTemplateEditProjectOrRequestMeta } from '~/entities/projects'
import { templateId } from '~/entities/projects/model/const'
import { navigateAfterTemplateSave } from '~/entities/projects/model/navigateAfterTemplateSave'
import { validateTemplateImages } from '~/entities/projects/model/templateImageValidation'
import {
  twoTemplateAdditionalSectionSchema,
  twoTemplateHeaderSchema,
  twoTemplateLeftHeaderSchema,
  twoTemplateRightHeaderSchema,
} from '~/entities/projects/model/templates-schema'
import { uploadProjectImage } from '~/entities/projects/model/uploadProjectImage'
import { useTemplateEditButtons } from '~/entities/projects/model/useTemplateEditButtons'
import { useTemplateManager } from '~/entities/projects/model/useTemplateManager'
import LandingButtonTypePopup from '~/entities/projects/ui/templates/LandingButtonTypePopup.vue'
import TemplateLandingButtonSlot from '~/entities/projects/ui/templates/TemplateLandingButtonSlot.vue'
import TemplateMediaSlot from '~/entities/projects/ui/templates/TemplateMediaSlot.vue'
import { permissions, useLoading } from '~/shared/lib'
import {
  BaseButton,
  BaseForm,
  BaseIsland,
  BaseNavigationBar,
} from '~/shared/ui'

const router = useRouter()
const route = useRoute()

const { uploadFromBlobUrl } = uploadProjectImage()
const { isProjectRequestContext, resolvedProjectTitle, projectId, routeParamForLinks } = useTemplateEditProjectOrRequestMeta()
const { hasExistingTemplate, existingTemplate, saveTemplate } = useTemplateManager(() =>
  isProjectRequestContext.value
    ? { projectRequestId: route.params.id as string }
    : { projectId: projectId.value },
)
const loading = useLoading()

const formRef1 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef2 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef3 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef4 = ref<ComponentPublicInstance<IFormExpose>>()
const images = ref<string[]>([])

const formModel = ref<ITwoTemplateFormData>({
  title: '',
  description: '',
  leftSectionTitle: '',
  leftSectionDescription: '',
  rightSectionTitle: '',
  rightSectionDescription: '',
  bottomSectionTitle: '',
  bottomSectionDescription: '',
})

const {
  buttons,
  isButtonTypePopupOpen,
  loadButtonsFromContent,
  handleStartAddButton,
  handleOpenModuleSelect,
  handleChooseButtonType,
  handleClearButton,
  getButtonsForSave,
  clearDraft,
} = useTemplateEditButtons({
  templateNumericId: templateId.two_template,
  editRoutePermission: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.TWO_TEMPLATE.EDIT.PERMISSION,
  getDraftPayload: () => ({
    formModel: { ...formModel.value },
    images: [...images.value],
    buttons: { ...buttons.value },
  }),
  applyDraftPayload: (draft) => {
    formModel.value = draft.formModel as ITwoTemplateFormData
    images.value = [...draft.images]
    buttons.value = { ...draft.buttons }
  },
})

watch(existingTemplate, (template) => {
  if (template?.content && hasExistingTemplate.value) {
    const content = template.content as unknown as ITwoTemplateData

    if (content.id === templateId.two_template) {
      formModel.value = {
        title: content.title || resolvedProjectTitle.value,
        description: content.description || '',
        leftSectionTitle: content.leftSectionTitle || '',
        leftSectionDescription: content.leftSectionDescription || '',
        rightSectionTitle: content.rightSectionTitle || '',
        rightSectionDescription: content.rightSectionDescription || '',
        bottomSectionTitle: content.bottomSectionTitle || '',
        bottomSectionDescription: content.bottomSectionDescription || '',
      }

      if (content.images) {
        images.value = [
          content.images.mainImage || '',
          content.images.sideTop || '',
          content.images.sideBottom || '',
          content.images.triple1 || '',
          content.images.triple2 || '',
          content.images.triple3 || '',
        ]
      }

      loadButtonsFromContent(content.buttons)
    }
  }
}, { immediate: true })

watch(
  resolvedProjectTitle,
  (name) => {
    if (name && !formModel.value.title) {
      formModel.value.title = name
    }
  },
  { immediate: true },
)

function handleUpdateImage(index: number, urls: string[]) {
  const url = urls[0] ?? ''
  const next = [...images.value]
  while (next.length <= index) {
    next.push('')
  }
  next[index] = url
  images.value = next
}

async function handleSave() {
  const validationResults = await Promise.all([
    formRef1.value?.validate(),
    formRef2.value?.validate(),
    formRef3.value?.validate(),
    formRef4.value?.validate(),
  ])

  const allValid = validationResults.every(result => result?.valid)

  if (!allValid) {
    return
  }
  const uploadPromises = images.value.map(url => url ? uploadFromBlobUrl(url) : Promise.resolve(null))
  const uploadedUrls = await Promise.all(uploadPromises)

  const finalImages = images.value.map((image, index) =>
    uploadedUrls[index] || image,
  )

  if (!validateTemplateImages({
    requiredImageCount: 6,
    images: finalImages,
  })) {
    return
  }

  await loading.wrapLoading(async () => {
    const templateContent = {
      id: templateId.two_template,
      title: resolvedProjectTitle.value,
      description: formModel.value.description,
      leftSectionTitle: formModel.value.leftSectionTitle,
      leftSectionDescription: formModel.value.leftSectionDescription,
      rightSectionTitle: formModel.value.rightSectionTitle,
      rightSectionDescription: formModel.value.rightSectionDescription,
      bottomSectionTitle: formModel.value.bottomSectionTitle,
      bottomSectionDescription: formModel.value.bottomSectionDescription,
      buttons: getButtonsForSave(),
      images: {
        mainImage: finalImages[0],
        sideTop: finalImages[1],
        sideBottom: finalImages[2],
        triple1: finalImages[3],
        triple2: finalImages[4],
        triple3: finalImages[5],
      },
    }

    await saveTemplate(templateContent as ITwoTemplateData)
    clearDraft()

    await navigateAfterTemplateSave(
      router,
      route.query,
      isProjectRequestContext.value
        ? (route.params.id as string)
        : routeParamForLinks.value,
    )
  })
}
</script>

<style lang="scss" scoped>
.two-template-edit {
  display: grid;
  gap: rem(48px);
  grid-template-columns: 1fr;

  &__header {
    display: grid;
    gap: rem(16px);
  }

  &__main-section {
    display: grid;
    grid-template-columns: 3fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__main-image {
    display: grid;
  }

  &__side-images {
    display: grid;
    gap: rem(20px);
  }

  &__dual-headers {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__header-left,
  &__header-right {
    display: grid;
    gap: rem(16px);
  }

  &__triple-images {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__additional-section {
    display: grid;
    gap: rem(16px);
  }

}
</style>
